import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Card, CardContent, Typography } from "@mui/material";

import { useFormState } from "./hooks/useFormState";
import { useTherapist } from "./hooks/useTherapist";
import { useFormLifecycle, confirmSaveDraft, confirmSubmitForm } from "./hooks/useFormLifecycle";
import { usePdfInstructions } from "./hooks/usePdfInstructions";

import { getTranslations } from "./translations";
import { getFormLanguage } from "./utils/languageDetection";
import { validateForm } from "./utils/validation";

import { FormHeader } from "./components/FormHeader";
import { FormSuccessScreen } from "./components/FormSuccessScreen";
import { FormStatusAlert } from "./components/FormStatusAlert";
import { LanguageSelectorCard } from "./components/LanguageSelectorCard";
import { InstructionsCard } from "./components/InstructionsCard";
import { SampleCollectionVideoCard } from "./components/SampleCollectionVideoCard";
import { TestInfoSection } from "./components/TestInfoSection";
import { MandatoryInfoSection } from "./components/MandatoryInfoSection";
import { AdditionalEmailSection } from "./components/AdditionalEmailSection";
import { AdditionalInfoSection } from "./components/AdditionalInfoSection";
import { TherapistSection } from "./components/TherapistSection";
import { TestSymptomsSection } from "./components/TestSymptomsSection";
import { DeclarationSection } from "./components/DeclarationSection";
import { FormActions } from "./components/FormActions";
import { SaveDialog } from "./components/SaveDialog";
import { SubmitDialog } from "./components/SubmitDialog";
import { FormSnackbar } from "./components/FormSnackbar";

const HTML_LANG = { english: "en", german: "de", french: "fr", dutch: "nl" };

export function SiboForm() {
  const { labTestId } = useParams();

  // Core state — start in loading if we have a labTestId so we never flash "not editable"
  const {
    formData,
    setFormData,
    handleInputChange,
    handleNestedChange,
    handleTestSymptomChange,
    handleOthersDescriptionChange,
    handleOthersMinutesChange,
    addOtherSymptom,
    removeOtherSymptom,
    updateAdditionalEmail,
    addAdditionalEmail,
    removeAdditionalEmail,
    isFormEditable,
  } = useFormState(labTestId);

  const [orderLanguage, setOrderLanguage] = useState("");
  const [manualLanguage, setManualLanguage] = useState(() => {
    if (labTestId) return sessionStorage.getItem(`sibo_lang_manual_${labTestId}`) || null;
    return null;
  });
  const [loadingFormData, setLoadingFormData] = useState(Boolean(labTestId));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [submitted, setSubmitted] = useState(false);

  // Language: manual selection takes priority over the order's language
  const formLanguage = manualLanguage || getFormLanguage(orderLanguage);
  const t = getTranslations(formLanguage);

  // Keep <html lang> in sync with the displayed language. With the static
  // lang="en", mobile browsers offered to auto-translate DE/FR/NL forms, and a
  // translated DOM made React crash (blank page) when Submit was pressed.
  useEffect(() => {
    const previous = document.documentElement.lang;
    document.documentElement.lang = HTML_LANG[formLanguage] || "en";
    return () => {
      document.documentElement.lang = previous;
    };
  }, [formLanguage]);

  const handleLanguageChange = (lang) => {
    setManualLanguage(lang);
    if (labTestId) sessionStorage.setItem(`sibo_lang_manual_${labTestId}`, lang);
  };

  // Therapist state
  const {
    therapistOptions,
    filteredTherapists,
    showTherapistSuggestions,
    setShowTherapistSuggestions,
    loadingTherapists,
    isSearching,
    loadInitialTherapists,
    handleTherapistSearch,
    handleTherapistSelection,
    handleCustomTherapist,
  } = useTherapist({ setFormData, t });

  // Lifecycle effects
  useFormLifecycle({
    labTestId,
    formData,
    setFormData,
    setOrderLanguage,
    loadingFormData,
    setLoadingFormData,
    setSnackbar,
    loadInitialTherapists,
    t,
  });

  // PDF instructions
  const { pages, downloadPdf } = usePdfInstructions(formLanguage);

  // Handlers
  const handleSave = () => setSaveDialogOpen(true);

  const handleSubmit = () => {
    const errors = validateForm(formData);
    setValidationErrors(errors);
    if (Object.keys(errors).length === 0) {
      setSubmitDialogOpen(true);
    } else {
      setSnackbar({ open: true, message: t.pleaseFillRequiredFields, severity: "error" });
    }
  };

  const handleConfirmSave = () =>
    confirmSaveDraft({ formData, setSnackbar, setSaveDialogOpen, t });

  const handleConfirmSubmit = () =>
    confirmSubmitForm({
      formData,
      therapistOptions,
      setSnackbar,
      setSubmitDialogOpen,
      setIsSubmitting,
      onSubmitSuccess: () => setSubmitted(true),
      t,
    });

  const isEditable = isFormEditable();

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f4f6f9", py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <FormHeader />

      {submitted ? (
        <FormSuccessScreen onEdit={() => setSubmitted(false)} t={t} />
      ) : (
        <>
          <FormStatusAlert
            loadingFormData={loadingFormData}
            isEditable={isEditable || loadingFormData}
            t={t}
          />

          {/* Hero card */}
          <Box sx={{ maxWidth: 800, mx: "auto", mb: 3 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid #dbeafe",
                borderRadius: 2,
                textAlign: "center",
                background: "linear-gradient(135deg, #f0f7ff 0%, #ffffff 55%, #eef5ff 100%)",
              }}
            >
              <CardContent sx={{ px: { xs: 3, sm: 5 }, py: { xs: 3, sm: 4 } }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, mb: 1.5, color: "#1a1a2e", letterSpacing: "-0.5px" }}
                >
                  {t.informationForm}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280", lineHeight: 1.8, maxWidth: 560, mx: "auto" }}>
                  {t.formDescription}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <LanguageSelectorCard language={formLanguage} onLanguageChange={handleLanguageChange} />

          <InstructionsCard pages={pages} downloadPdf={downloadPdf} t={t} />

          <SampleCollectionVideoCard t={t} />

          {/* Main form card */}
          <Box sx={{ maxWidth: 800, mx: "auto", mb: 4 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid #dfdfdf",
                borderRadius: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
              }}
            >
              <CardContent sx={{ px: { xs: 3, sm: 4, md: 5 }, py: { xs: 3, sm: 4 } }}>
                <TestInfoSection
                  formData={formData}
                  loadingFormData={loadingFormData}
                  t={t}
                />

                <MandatoryInfoSection
                  formData={formData}
                  handleInputChange={handleInputChange}
                  isEditable={isEditable}
                  validationErrors={validationErrors}
                  t={t}
                />

                <AdditionalEmailSection
                  formData={formData}
                  updateAdditionalEmail={updateAdditionalEmail}
                  addAdditionalEmail={addAdditionalEmail}
                  removeAdditionalEmail={removeAdditionalEmail}
                  isEditable={isEditable}
                  t={t}
                />

                <AdditionalInfoSection
                  formData={formData}
                  handleNestedChange={handleNestedChange}
                  setFormData={setFormData}
                  isEditable={isEditable}
                  validationErrors={validationErrors}
                  t={t}
                />

                <TherapistSection
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleTherapistSearch={handleTherapistSearch}
                  handleTherapistSelection={handleTherapistSelection}
                  handleCustomTherapist={handleCustomTherapist}
                  showTherapistSuggestions={showTherapistSuggestions}
                  setShowTherapistSuggestions={setShowTherapistSuggestions}
                  filteredTherapists={filteredTherapists}
                  loadingTherapists={loadingTherapists}
                  isSearching={isSearching}
                  isEditable={isEditable}
                  validationErrors={validationErrors}
                  t={t}
                />

                <TestSymptomsSection
                  formData={formData}
                  handleTestSymptomChange={handleTestSymptomChange}
                  handleOthersDescriptionChange={handleOthersDescriptionChange}
                  handleOthersMinutesChange={handleOthersMinutesChange}
                  addOtherSymptom={addOtherSymptom}
                  removeOtherSymptom={removeOtherSymptom}
                  isEditable={isEditable}
                  validationErrors={validationErrors}
                  t={t}
                />

                <DeclarationSection
                  formData={formData}
                  handleInputChange={handleInputChange}
                  isEditable={isEditable}
                  validationErrors={validationErrors}
                  t={t}
                />

                {isEditable && (
                  <FormActions
                    onSave={handleSave}
                    onSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    t={t}
                  />
                )}
              </CardContent>
            </Card>
          </Box>

          <SaveDialog
            open={saveDialogOpen}
            onConfirm={handleConfirmSave}
            onCancel={() => setSaveDialogOpen(false)}
            t={t}
          />

          <SubmitDialog
            open={submitDialogOpen}
            onConfirm={handleConfirmSubmit}
            onCancel={() => setSubmitDialogOpen(false)}
            isSubmitting={isSubmitting}
            t={t}
          />
        </>
      )}

      <FormSnackbar
        snackbar={snackbar}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}
